/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument*/
// @ts-nocheck
// TODO(ross) 2026.05.28 - clean up comments
import { useState, useRef, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { Typography, Button } from '@mui/material';
import { ColorMode } from 'theme';
import * as fc from 'd3fc';
import * as d3 from 'd3';
import { timeFormat } from 'd3-time-format';
import * as trading_signals from 'trading-signals';
import {
  timeYear,
  timeMonth,
  timeWeek,
  timeDay,
  timeHour,
  timeMinute,
  timeSecond,
} from 'd3-time';
import sortBy from 'lodash/sortBy';
import sortedUniqBy from 'lodash/sortedUniqBy';
import range from 'lodash/range';
import sortedIndexBy from 'lodash/sortedIndexBy';
import debounce from 'lodash/debounce';

import {
  OPTION_ORIGIN_TO_PALETTE,
  OPTION_ACCUM_TO_PALETTE,
  HiroAlertCategoryFromAlertCategory,
  HiroLineType,
  genericSymbolineParamsEqual,
} from 'types';
import {
  hiroChartTimeRangeState,
  timezoneState,
  stocksState_SUBSCRIBE_TO_POLLING_RENDER,
  currentSymHiroOverviewState,
  workerState,
  hiroAlertsState,
} from 'states';
import { unsupressedSetInterval } from 'util/poll';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { formatAsCompactNumber } from 'util/shared/common';
import { dayjs, ET } from 'util/shared/date';
import { useLog } from 'hooks';
import { useHiroKeyLevels } from 'hooks/hiro/useHiroKeyLevels';
import { useHiroCtx } from './HiroProvider';
import { useStreamingSocketCore } from 'hooks/bloomberg/streaming/useStreamingSocket';

import { HISTORY_BEGIN, HiroChartDatafeed } from './HiroDatafeed';
import { filterHiroAlerts } from 'util/hiro';
import {
  ChartSeries,
  DeltaChartSeries,
  PriceChartSeries,
  KeyLevelChartSeries,
  AlertsChartSeries,
  IndicatorSingleLineChartSeries,
  BollingerBandsChartSeries,
  MACDChartSeries,
  candlesYExtentAccessors,
  indicatorYExtentAccessors,
} from './HiroChartSeries';

import {
  hiroIndicatorValid,
  indicatorFromName,
  IndicatorResultType,
} from './HiroChart/controls/AvailableIndicators';
import { skipNonMarketHours } from 'components/sg_d3fc/skip_non_market_hours';

// consider a point as worthy of being highlighted if it is within the following
// percentage of the X axis away from where the cursor is.
const PLOT_CURSOR_HIGHLIGHT_POINT_X_PROPORTION_OFFSET_MAX = 0.05;

const HIDE_LABELS_PAST_VISIBLE_DAYS = 0.6;
const Y_AXIS_WIDTH = 48;

interface HiroD3ChartProps {
  chartId: string;
  sym: string;
  deltaSeriesList: ChartSeries[];
  priceSeriesList: ChartSeries[];
  mainIndicatorList: ChartSeries[];
  bottomIndicatorList: ChartSeries[];
  hiroKeyLevels: ChartSeries;
  alertSeries: ChartSeries;
  alerts: ChartSeries;
  rerenderCnt: number;
  datafeedRef: Ref<HiroChartDatafeed>;
  hiroLineType: HiroLineType;
  resetZoomRef: Ref;
}

const HiroD3Chart = ({
  chartId,
  sym,
  deltaSeriesList,
  priceSeriesList,
  mainPaneIndicatorList,
  bottomIndicatorList,
  hiroKeyLevels,
  alertSeries,
  rerenderCnt,
  datafeedRef,
  hiroLineType,
  resetZoomRef,
}: HiroD3ChartProps) => {
  const theme = useTheme();
  const timeZone = useRecoilValue(timezoneState);
  const { priceLineType, includeHiroETH: showExtendedHours } = useHiroCtx();

  const zoomTransformRef = useRef(null);

  const [autoRangeYAxes, setAutoRangeYAxes] = useState(true);
  useEffect(() => {
    if (autoRangeYAxes) {
      resetZoomRef.current?.();
    }
  }, [autoRangeYAxes, resetZoomRef]);
  const autoRangeYAxesRef = useRef(autoRangeYAxes);
  autoRangeYAxesRef.current = autoRangeYAxes;

  const yExtent = fc
    .extentLinear()
    .accessors(candlesYExtentAccessors)
    .pad([0.1, 0.1]);

  const botIndicatorsYExtent = fc
    .extentLinear()
    .accessors(indicatorYExtentAccessors)
    .pad([0.05, 0.05]);

  const xExtent = fc
    .extentDate()
    .accessors([
      (series) =>
        series.candles?.reduce(
          (accum, c) => Math.max(accum ?? c.date, c.date),
          null,
        ),
      (series) =>
        series.candles?.reduce(
          (accum, c) => Math.min(accum ?? c.date, c.date),
          null,
        ),
    ])
    .pad([0.0, 0.07]);

  const deltaAndPriceSeries = [...deltaSeriesList, ...priceSeriesList];
  const deltaAndPriceSeriesRef = useRef([]);
  deltaAndPriceSeriesRef.current = deltaAndPriceSeries;
  const allSeriesData = [
    ...deltaSeriesList,
    ...priceSeriesList,
    hiroKeyLevels,
    alertSeries,
    ...mainPaneIndicatorList,
  ];

  const xDomain = xExtent(deltaAndPriceSeries);
  //if (isNaN(xDomain[0])) {
  //  xDomain = [new Date(), new Date()];
  //}
  // TODO(ross) 2026.05.06 - scaleDiscontinuous

  const chartXScaleRef = useRef(null);
  const setXScaleRef = () => {
    if (showExtendedHours) {
      chartXScaleRef.current = d3.scaleTime();
    } else {
      const scaleTime = d3.scaleTime();
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const oldTicks = scaleTime.ticks;
      const newTicks = (...args) =>
        sortedUniqBy(
          // eslint-disable-next-line @typescript-eslint/unbound-method
          oldTicks(...args).map((d) => skipNonMarketHours.clampUp(d)),
          (d) => +d,
        );

      scaleTime.ticks = newTicks;

      // eslint-disable-next-line @typescript-eslint/unbound-method
      const oldCopy = scaleTime.copy;
      scaleTime.copy = () => {
        const result = oldCopy();
        result.ticks = newTicks;
        return result;
      };

      const scaleDiscontinuous = fc
        .scaleDiscontinuous(scaleTime)
        .discontinuityProvider(skipNonMarketHours);

      chartXScaleRef.current = scaleDiscontinuous;

      chartXScaleRef.current.domain(xDomain).range([0, 640]);
    }
  };
  if (chartXScaleRef.current == null) {
    setXScaleRef();
  }
  // only sensitive to `showExtendedHours`.  The rest of the system continues to
  // update the other enclosed variables as needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setXScaleRef, [showExtendedHours]);

  const chartYScaleRef = useRef(d3.scaleLinear());

  const botIndicatorsYScaleRef = useRef([]);
  botIndicatorsYScaleRef.current.length = Math.min(
    bottomIndicatorList.length,
    botIndicatorsYScaleRef.current.length,
  );
  range(bottomIndicatorList.length - botIndicatorsYScaleRef.current.length).map(
    (_) => botIndicatorsYScaleRef.current.push(d3.scaleLinear()),
  );

  const priceScaleRef = useRef(d3.scaleLinear());
  const priceExtentRef = useRef();
  priceExtentRef.current = yExtent(priceSeriesList);
  if (zoomTransformRef.current == null) {
    priceScaleRef.current.domain(priceExtentRef.current);
  }

  const unzoomedDeltaDomainRef = useRef();
  unzoomedDeltaDomainRef.current = yExtent(
    deltaSeriesList.length ? deltaSeriesList : priceSeriesList,
  );
  let yDomain;
  if (
    zoomTransformRef.current != null &&
    !autoRangeYAxes &&
    !isNaN(chartYScaleRef.current.domain()[0])
  ) {
    yDomain = chartYScaleRef.current.domain();
  } else {
    yDomain = unzoomedDeltaDomainRef.current;
    chartYScaleRef.current.domain(yDomain);
  }

  const botIndicatorsYDomains = bottomIndicatorList.map((bottomIndicator) =>
    botIndicatorsYExtent([bottomIndicator]),
  );

  const chartXDomain =
    isNaN(chartXScaleRef.current.domain()[0]) ||
    +chartXScaleRef.current.domain()[0] < HISTORY_BEGIN.valueOf()
      ? xDomain
      : chartXScaleRef.current.domain();

  const candleWidth = isNaN(chartXDomain[0])
    ? 2
    : (0.5 *
        (chartXScaleRef.current.range()[1] -
          chartXScaleRef.current.range()[0]) *
        datafeedRef.current.candleDurationMs) /
      (chartXDomain[1].valueOf() - chartXDomain[0].valueOf());

  const deltaScale = d3.scaleLinear().domain(yDomain);
  const deltaAxis = d3
    .axisRight(chartYScaleRef.current)
    .ticks(10)
    .tickFormat((d) => formatAsCompactNumber(d));

  const priceInDeltaScale = (p) => {
    const deltaDomain = unzoomedDeltaDomainRef.current;
    const priceDomain = priceExtentRef.current;
    const factor = isNaN(deltaDomain[0])
      ? 1
      : (deltaDomain[1] - deltaDomain[0]) / (priceDomain[1] - priceDomain[0]);

    const result = (p - priceDomain[0]) * factor + (deltaDomain[0] ?? 0);
    //if (zoomTransformRef.current != null) {
    //  let nextResult = zoomTransformRef.current.applyY(result)
    //  console.log(result, nextResult);
    //  result = nextResult;
    //}
    return result;
  };

  const deltaInPriceScale = (d) => {
    const deltaDomain = unzoomedDeltaDomainRef.current;
    const priceDomain = priceExtentRef.current;
    const factor = isNaN(deltaDomain[0])
      ? 1
      : (priceDomain[1] - priceDomain[0]) / (deltaDomain[1] - deltaDomain[0]);

    return (d - deltaDomain[0]) * factor + (priceDomain[0] ?? 0);
  };

  //const priceInDeltaScale = (p) => {
  //  const result =
  //    (p - priceScaleRef.current.domain()[0]) *
  //      deltaExtantFromPriceExtentFactor +
  //    (chartYScaleRef.current.domain()[0] ?? 0);
  //  if (zoomTransformRef.current != null) {
  //    return zoomTransformRef.current.applyY(result);
  //  } else {
  //    return result;
  //  }
  //};

  const multi = fc
    .seriesSvgMulti()
    .series([
      ...allSeriesData.flatMap((chartSeries) =>
        chartSeries.d3fcSeries(
          xDomain,
          priceScaleRef,
          chartXScaleRef,
          chartYScaleRef,
          priceInDeltaScale,
          candleWidth,
          theme.colorMode,
          hiroLineType,
        ),
      ),
      fc.annotationSvgGridline(),
    ])
    .mapping((data, index, _series) => {
      let adjustedIdx = index;
      for (const chartSeries of data) {
        const numD3fcSeries = chartSeries.numD3fcSeries();

        if (adjustedIdx < numD3fcSeries) {
          return chartSeries.d3Data();
        }
        adjustedIdx -= numD3fcSeries;
      }
    });

  const botIndicatorsMulti = bottomIndicatorList.map((bottomIndicator, i) =>
    fc
      .seriesSvgMulti()
      .series([
        ...bottomIndicator.d3fcSeries(
          xDomain,
          priceScaleRef,
          chartXScaleRef,
          botIndicatorsYScaleRef.current[i],
          priceInDeltaScale,
          candleWidth,
          theme.colorMode,
          hiroLineType,
        ),
        fc.annotationSvgGridline(),
      ])
      .mapping((data, index, _series) => {
        let adjustedIdx = index;
        for (const chartSeries of data) {
          const numD3fcSeries = chartSeries.numD3fcSeries();

          if (adjustedIdx < numD3fcSeries) {
            return chartSeries.d3Data();
          }
          adjustedIdx -= numD3fcSeries;
        }
      }),
  );

  const timeFormatter = genTimeTickFormat(timeZone);

  const crosshair = fc
    .annotationSvgCrosshair()
    .xScale(chartXScaleRef.current)
    .yScale(chartYScaleRef.current)
    .yLabel((_d) => '')
    .xLabel((d) =>
      dayjs(
        new Date(
          dayjs(chartXScaleRef.current.invert(d.x))
            .toDate()
            .toLocaleString('en-US', {
              timeZone,
            }),
        ),
      ).format('MMM D h:mm:ss'),
    )
    .decorate((selection) => {
      selection
        .select('g.vertical text')
        .attr('font-size', '11')
        .attr('fill', 'currentColor');
      selection.selectAll('.point>path').style('display', 'none');
    });

  const onMouseMove = (plotSvg, event) => {
    const { clientX, clientY } = event;
    const {
      x: plotXOffset,
      y: plotYOffset,
      width: plotWidth,
    } = plotSvg.node().getBoundingClientRect();
    const cursorX = clientX - plotXOffset;
    const cursorY = clientY - plotYOffset;
    plotSvg.datum([{ x: cursorX, y: cursorY }]).call(crosshair);

    // find the closest points and line to highlight and display
    const cursorDate = chartXScaleRef.current.invert(cursorX);

    let candidateSeriesList = deltaAndPriceSeries.map((series) => {
      const gteIdx = sortedIndexBy(
        series.candles,
        { date: cursorDate },
        (c) => c.date,
      );

      let closestCandle = null;

      for (const candleIdx of [gteIdx - 1, gteIdx]) {
        const c = series.candles[candleIdx];
        if (c == null) {
          continue;
        }
        if (
          closestCandle == null ||
          Math.abs(cursorDate.valueOf() - c.date.valueOf()) <
            Math.abs(cursorDate.valueOf() - closestCandle.date.valueOf())
        ) {
          closestCandle = c;
        }
      }

      let xDiffToCursor = null;
      let yDiffToCursor = null;

      if (closestCandle != null) {
        xDiffToCursor = Math.abs(
          cursorX - chartXScaleRef.current(closestCandle.date),
        );

        for (const val of [closestCandle.high, closestCandle.low]) {
          const candleY = chartYScaleRef.current(
            series instanceof PriceChartSeries ? priceInDeltaScale(val) : val,
          );
          const thisYDiffToCursor = Math.abs(cursorY - candleY);
          if (yDiffToCursor == null || thisYDiffToCursor < yDiffToCursor) {
            yDiffToCursor = thisYDiffToCursor;
          }
        }
      }
      return {
        series,
        closestCandle,
        xDiffToCursor,
        yDiffToCursor,
      };
    });

    candidateSeriesList = sortBy(candidateSeriesList, (s) => s.yDiffToCursor);
    const candidateSeries = candidateSeriesList[0];
    if (
      candidateSeries?.xDiffToCursor != null &&
      candidateSeries?.yDiffToCursor != null &&
      candidateSeries?.xDiffToCursor <=
        plotWidth * PLOT_CURSOR_HIGHLIGHT_POINT_X_PROPORTION_OFFSET_MAX
    ) {
      const t = candidateSeries.closestCandle.time;

      const indicatorLabels = (prefixGenerator) => (ind) => {
        const indAry = ind.d3Data();
        const idx = sortedIndexBy(indAry, { time: t }, (c) => c.time);
        if (idx < 0 || idx >= indAry.length) {
          return;
        }
        const indDatum = indAry[idx];
        if (indDatum.time !== t) {
          return;
        }
        const prefix = prefixGenerator?.(ind) ?? '';

        return indicatorDisplayStrings(
          ind.indicatorMeta.resultType,
          ind.yScale,
          indDatum,
        ).map((valStr, idx) => ({
          color: ind.colors?.[idx] ?? ind.colors?.[0],
          text: `${prefix}${valStr}`,
        }));
      };

      candidateSeriesList = sortBy(candidateSeriesList, [
        (cs) => cs.series.name,
      ]).filter((cs) => cs.closestCandle.time == t);
      const closestCandleDisplay = plotSvg
        .selectAll('g.current-candle-display')
        .data([
          ...candidateSeriesList.map((cs) => {
            const fmt =
              cs.series instanceof PriceChartSeries
                ? currencyLabel
                : deltaLabel;
            let candleStr;
            if (
              (cs.series.symbolLineParams.kind == 'price' &&
                priceLineType !== 'Line') ||
              (cs.series.symbolLineParams.kind == 'hiro' &&
                hiroLineType !== 'Line')
            ) {
              const { open, high, low, close, volume } = cs.closestCandle;
              candleStr = `O: ${fmt(open)} H: ${fmt(high)} L: ${fmt(low)} C: ${fmt(close)}`;
              if ((volume ?? 0) != 0) {
                const volStr = formatAsCompactNumber(volume, {
                  maximumSignificantDigits: 3,
                });
                candleStr += ` V: ${volStr}`;
              }
            } else {
              candleStr = fmt(cs.closestCandle.close);
            }
            return {
              color: cs.series.colors[0],
              text: `${cs.series.name}: ${candleStr}`,
            };
          }),
          ...mainPaneIndicatorList
            .flatMap(indicatorLabels((ind) => `${ind.name}: `))
            .filter(Boolean),
        ]);

      const closestCandleDisplayG = closestCandleDisplay
        .enter()
        .append('g')
        .attr('class', 'current-candle-display')
        .attr('font-size', '12')
        .attr('transform', (_, i) => `translate(9, ${28 + 12 * i})`);
      closestCandleDisplayG.exit().remove();

      closestCandleDisplayG.append('text');

      closestCandleDisplay
        .merge(closestCandleDisplayG)
        .attr('fill', ({ color }) => color)
        .select('text')
        .text(({ text }) => text);

      for (const bottomIndicator of bottomIndicatorList) {
        const bottomChart = d3.select(`#${bottomIndicator.id(hiroLineType)}`);
        const closestCandleDisplay = bottomChart
          .selectAll('g.current-candle-display')
          .data(indicatorLabels()(bottomIndicator)?.filter(Boolean) ?? []);

        const closestCandleDisplayG = closestCandleDisplay
          .enter()
          .append('g')
          .attr('class', 'current-candle-display')
          .attr('font-size', '12')
          .attr('transform', (_, i) => `translate(9, ${14 + 12 * i})`);
        closestCandleDisplayG.exit().remove();

        closestCandleDisplayG.append('text');

        closestCandleDisplay
          .merge(closestCandleDisplayG)
          .attr('fill', ({ color }) => color)
          .select('text')
          .text(({ text }) => text);
      }
    }

    for (const bottomIndicator of bottomIndicatorList) {
      const bottomChart = d3.select(`#${bottomIndicator.id(hiroLineType)}`);

      const pathJoin = bottomChart
        .selectAll('line.bot-indicator-crosshair-line')
        .data([cursorX]);

      const createdPaths = pathJoin
        .enter()
        .append('line')
        .attr('class', 'bot-indicator-crosshair-line')
        .attr('fill', 'none')
        .attr('stroke', '#fff');

      pathJoin.merge(createdPaths).attr('x1', cursorX).attr('y2', 1e6);
    }
  };
  const onMouseMoveRef = useRef(onMouseMove);
  onMouseMoveRef.current = onMouseMove;

  const renderRef = useRef<() => void, null>(null);

  const zoomRef = useRef(
    fc.zoom().on('zoom', (e) => {
      zoomTransformRef.current = e.transform;

      renderRef.current?.();

      priceScaleRef.current.domain(
        chartYScaleRef.current.domain().map(deltaInPriceScale),
      );

      // Request a redraw — only re-fires 'draw' events on existing elements.
      // Does NOT re-run enter/exit or decorate callbacks.
      //document.getElementById(chartId)?.requestRedraw();

      const timeRange = chartXScaleRef.current.domain();
      if (!isNaN(timeRange[0])) {
        datafeedRef.current.debouncedOnVisibleRangeChanged({
          from: dayjs(timeRange[0]),
          to: dayjs(timeRange[1]),
        });
      }
    }),
  );

  const chart = fc
    .chartCartesian(chartXScaleRef.current, chartYScaleRef.current)
    .yTickFormat((_d) => '') // Blank and drawn in `draw.y-axis` event so the z-ordering is correct w.r.t. curent price label
    .xTickFormat((d) => d != null && timeFormatter(d))
    .svgPlotArea(multi);
  if (botIndicatorsMulti.length > 0) {
    // suppress tick creation on main pane and place on bottom indicator
    chart.xTicks(0);
  }

  const botIndicatorsCharts = botIndicatorsMulti.map((multi, i) => {
    const botChart = fc
      .chartCartesian(chartXScaleRef.current, botIndicatorsYScaleRef.current[i])
      .xTickFormat((d) => d != null && timeFormatter(d))
      .yTicks(3)
      .svgPlotArea(multi)
      .xDomain(chartXDomain)
      .yDomain(botIndicatorsYDomains[i])
      .decorate((selection) => {
        selection
          .enter()
          // additionally add a d3fc-svg element for the axis
          .append('d3fc-svg')
          // move the element into the right-axis cell
          .style('grid-column', 1)
          .style('grid-row', 3)
          // and set the axis width
          .style('width', `${Y_AXIS_WIDTH}px`);

        selection.on('measure.y-axis', (event) => {
          botIndicatorsYScaleRef.current[i].range([
            d3.select(event.currentTarget).select('d3fc-svg.y-axis').node()
              .offsetHeight,
            0,
          ]);
        });
      });

    if (i < botIndicatorsMulti.length - 1) {
      // suppress tick creation to only the bottom indicator
      botChart.xTicks(0);
    }

    return botChart;
  });

  chart.xDomain(chartXDomain);
  //chart.yDomain(yDomain);
  if (zoomTransformRef.current == null || autoRangeYAxes) {
    chart.yDomain(yDomain); // isNaN(chartYScaleRef.current.domain()[0]) ? yDomain : chartYScaleRef.current.domain());
  }

  const setZoomNodeState = (
    selection,
    includeX: boolean,
    includeY: boolean,
  ) => {
    const xScale = includeX ? chartXScaleRef.current : null;
    const yScale =
      includeY && !autoRangeYAxesRef.current ? chartYScaleRef.current : null;
    if (xScale == null && yScale == null) {
      selection.on('.zoom', null);
    } else {
      zoomRef.current(selection, xScale, yScale);
    }
  };

  const setAllZoomNodeStates = (selection) => {
    selection.selectAll('.plot-area').call(setZoomNodeState, true, true);
    selection.selectAll('.x-axis').call(setZoomNodeState, true, false);
    selection
      .selectAll('.y-axis, .price-axis, .delta-axis-zoom-listener-overlay')
      .call(setZoomNodeState, false, true);
  };
  useEffect(() => {
    d3.select(`#${chartId}`).call(setAllZoomNodeStates);
    resetZoomRef.current?.();
    // only level sensitivty that should trigger this block is `autoRangeYAxes`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRangeYAxes]);

  chart.decorate((selection) => {
    const plotContainer = selection.enter().select('d3fc-svg.plot-area');
    const plotSvg = plotContainer.select('svg');
    plotSvg
      .on('draw', () => {
        plotSvg.datum(mouseLocation).call(crosshair);
      })
      .on('mousemove', (event) => onMouseMoveRef.current(plotSvg, event))
      .on('mouseout', (_event) => {
        plotSvg.datum([]).call(crosshair);
        plotSvg.selectAll('g.current-candle-display').remove();
        for (const bottomIndicator of bottomIndicatorList) {
          const plotContainer = d3.select(
            `#${bottomIndicator.id(hiroLineType)}`,
          );
          plotContainer.selectAll('line.bot-indicator-crosshair-line').remove();
          plotContainer.selectAll('g.current-candle-display').remove();
        }

        //plotContainer.requestRedraw();
      });

    // when the chart is added to the DOM
    selection
      .selectAll('d3fc-svg.price-axis')
      .data([true])
      .enter()
      // additionally add a d3fc-svg element for the axis
      .append('d3fc-svg')
      .attr('class', 'price-axis')
      .attr('id', 'hiroPriceScale')
      // move the element into the right-axis cell
      .style('grid-column', 1)
      .style('grid-row', 3)
      // and set the axis width
      .style('width', `${Y_AXIS_WIDTH}px`)
      // when there's a measure event (namespaced to avoid removing existing handlers)
      .on('measure.z-axis', (event) => {
        // set the range on the scale to the elements height
        priceScaleRef.current.range([event.detail.height, 0]);
      })
      .on('draw.z-axis', (event, _deltaAndPriceSeries) => {
        const axisG = d3
          .select(event.currentTarget)
          .select('svg')
          .selectAll('g.left-axis')
          .data([{}]);

        const axisGenter = axisG
          .enter()
          .append('g')
          .attr('class', 'left-axis')
          .attr('transform', `translate(${Y_AXIS_WIDTH}, 0)`);

        const priceAxis = fc.axisLeft(priceScaleRef.current).ticks(8);

        axisG.merge(axisGenter).call(priceAxis);

        drawYAxisClosingVals(
          d3.select(event.currentTarget).select('d3fc-svg#hiroPriceScale svg'),
          deltaAndPriceSeriesRef.current.filter(
            (s) => s instanceof PriceChartSeries,
          ),
          priceAxis.scale(),
          (n) =>
            Intl.NumberFormat('en', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(n),
        );
      });

    selection
      .selectAll('div.delta-axis-zoom-listener-overlay')
      .data([true])
      .enter()
      .append('div')
      .attr('class', 'delta-axis-zoom-listener-overlay')
      .style('grid-column', 4)
      .style('grid-row', 3)
      .style('width', `${Y_AXIS_WIDTH}px`)
      .style('height', '100%')
      .style('z-index', '999');

    selection
      .enter()
      .on('draw.y-axis', (event, _deltaAndPriceSeries) => {
        const deltaSeriesList = deltaAndPriceSeriesRef.current.filter(
          (s) => s instanceof DeltaChartSeries,
        );
        const yAxisSvg = d3
          .select(event.currentTarget)
          .select('d3fc-svg.y-axis.right-axis svg')
          .style('opacity', deltaSeriesList.length == 0 ? '0' : '1');
        yAxisSvg
          .append('g')
          .attr('class', 'delta-axis-override')
          .call(deltaAxis);

        drawYAxisClosingVals(
          yAxisSvg,
          deltaSeriesList,
          chartYScaleRef.current,
          (n) => formatAsCompactNumber(n, { maximumSignificantDigits: 3 }),
        );
      })
      .on('measure.y-axis', (event) => {
        deltaScale.range([
          d3.select(event.currentTarget).select('d3fc-svg.y-axis').node()
            .offsetHeight,
          0,
        ]);
      });
    selection.enter().call(setAllZoomNodeStates);
  });

  resetZoomRef.current = (xOnly?: boolean) => {
    zoomTransformRef.current = null;
    chartXScaleRef.current.domain([NaN, NaN]);
    if (!(xOnly ?? false)) {
      chartYScaleRef.current.domain([NaN, NaN]);
      priceScaleRef.current.domain([NaN, NaN]);
    }
  };
  useEffect(() => {
    if (zoomTransformRef.current != null) {
      resetZoomRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sym]);

  const lastSymRendered = useRef<string | null>(null);

  const render = () => {
    if (isNaN(xDomain[0])) {
      return;
    }
    const chartDiv = d3.select(`#${chartId}`);
    if (lastSymRendered.current != null && lastSymRendered.current != sym) {
      lastSymRendered.current = sym;
      chartDiv
        .selectAll(
          '.plot-area, .x-axis, .y-axis, .delta-axis-zoom-listener-overlay, .price-axis',
        )
        .each(function () {
          d3.select(this).call(zoomRef.current.transform, d3.zoomIdentity);
        });
      zoomTransformRef.current = null;
      chartXScaleRef.current.domain([NaN, NaN]);
    }
    //if (lastSymRendered.current == null) {
    //  lastSymRendered.current = sym;
    //}

    const validChartIds = allSeriesData.map((s) => s.id(hiroLineType));
    chartDiv.selectAll('.multi>g.chart-series-group').each(function (_, idx) {
      const sel = d3.select(this);
      if (sel.attr('id') != validChartIds[idx]) {
        sel.remove();
      }
    });

    chartDiv.datum(allSeriesData).call(chart);

    bottomIndicatorList.map((bottomIndicator, i) => {
      d3.select(`#${bottomIndicator.id(i)}`)
        .datum([bottomIndicator])
        .call(botIndicatorsCharts[i]);
    });

    d3.selectAll('g.hiro-closing-y-axis-label').raise();
  };

  renderRef.current = render;

  useEffect(() => {
    render();
    // skip ``render` dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deltaSeriesList,
    priceSeriesList,
    hiroKeyLevels,
    alertSeries,
    rerenderCnt,
    hiroLineType,
    sym,
    autoRangeYAxes,
  ]);

  const classes = ['d3fc-hiro-chart'];
  classes.push(
    `d3fc-chart-${theme.colorMode === ColorMode.LIGHT ? 'light' : 'dark'}`,
  );
  if (
    !isNaN(xDomain[0]) &&
    (xDomain[1].valueOf() - xDomain[0].valueOf()) / (24 * 60 * 60 * 1_000) >
      HIDE_LABELS_PAST_VISIBLE_DAYS
  ) {
    classes.push('labels-on-hover');
  }

  const mainPaneClasses = [...classes];
  if (bottomIndicatorList.length > 0) {
    mainPaneClasses.push('hide-bottom-axis');
  }

  return (
    <div
      style={{
        width: '100%',
        height: '95vh',
        display: 'grid',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flexGap: 0,
          gridArea: '1 / 1',
          paddingBottom: '12px',
        }}
      >
        <div
          style={{
            flexGrow: 3,
            width: '100%',
            display: 'grid',
          }}
        >
          <div
            id={chartId}
            style={{
              width: '100%',
              gridArea: '1 / 1',
            }}
            className={mainPaneClasses.join(' ')}
          />
          {/* Main Pane UI overlay */}
          <div
            style={{
              width: '100%',
              gridArea: '1 / 1',
            }}
          >
            <div
              style={{
                display: 'flex',
                height: '100%',
                width: '100%',
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <div></div>
              <div style={{ flexGrow: 1 }}></div>
              <div
                style={{
                  paddingBottom: '3px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'stretch',
                }}
              >
                <div>{/*Main Pane, buttom left corner */}</div>
                <div style={{ flexGrow: 1 }}></div>
                <div style={{ paddingRight: `${(2 / 3) * Y_AXIS_WIDTH}px` }}>
                  {/*Main Pane, buttom right corner */}
                  <Button
                    variant={autoRangeYAxes ? 'contained' : 'outlined'}
                    w={1}
                    sx={{
                      padding: 0,
                      fontSize: 11,
                      minWidth: '1.5em',
                      marginBottom:
                        bottomIndicatorList.length == 0 ? '2em' : undefined,
                    }}
                    onClick={() => setAutoRangeYAxes(!autoRangeYAxes)}
                    title={`Click to ${autoRangeYAxes ? 'disable' : 'enable'} auto-range for HIRO/Price axis.`}
                  >
                    A
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {bottomIndicatorList.map((indicator, i) => (
          <div
            key={indicator.id(hiroLineType)}
            style={{
              flexGrow: 1,
              width: '100%',
              marginTop: '-20px',
              paddingBottom: '4px',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                paddingLeft: `${Y_AXIS_WIDTH}px`,
                position: 'relative',
                top: '20px',
              }}
            >
              {indicator.name}
            </Typography>
            <div
              id={indicator.id(i)}
              style={{
                width: '100%',
                height: '100%',
              }}
              className={[
                ...classes,
                i < bottomIndicatorList.length - 1 ? 'hide-bottom-axis' : '',
              ].join(' ')}
            />
          </div>
        ))}
      </div>

      {/* full container UI overlay */}
      <div
        style={{
          width: '100%',
          gridArea: '1 / 1',
        }}
      >
        <div
          style={{
            display: 'flex',
            height: '100%',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <div></div>
          <div style={{ flexGrow: 1 }}></div>
          <div
            style={{
              paddingBottom: '3px',
              width: '100%',
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <div>{/*full container bottom left corner */}</div>
            <div style={{ flexGrow: 1 }}></div>
            <div style={{ paddingRight: `${(2 / 3) * Y_AXIS_WIDTH}px` }}>
              {/*full container bottom right corner */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const HiroChartV3 = () => {
  const {
    sym,
    includeHiroETH: showExtendedHours,
    hiroRollingWindowSeconds,
    selectedLenses,
    optionType,
    candleDuration,
    hiroLineType,
    priceLineType,
    showAlerts,
    showHiroKeyLevels: showKeyLevels,
    hiroIndicators,
    update,
    startQueryDate,
    endQueryDate,
  } = useHiroCtx();
  const { all: allTradesEnabled, nextExp: nextExpiryEnabled } = selectedLenses;
  const totalOptionAccumulationEnabled = optionType === 'total';
  const putOptionAccumulationEnabled = optionType === 'pc';
  const callOptionAccumulationEnabled = optionType === 'pc';
  const candleDurationMs = candleDuration * 1_000;

  const webWorker = useRecoilValue(workerState);
  const datafeed = useRef<undefined | HiroChartDatafeed>();
  const setHiroTimeRange = useSetRecoilState(hiroChartTimeRangeState);
  const theme = useTheme();
  const resetZoomRef = useRef();

  useEffect(
    () => datafeed.current?.onIntervalChanged(candleDurationMs),
    [candleDurationMs],
  );
  useEffect(() => {
    if (
      datafeed.current == null ||
      startQueryDate == null ||
      endQueryDate == null ||
      resetZoomRef.current == null ||
      startQueryDate.isAfter(endQueryDate, 'day')
    ) {
      return;
    }
    const now = dayjs().tz(ET);
    let to = null;
    if (!endQueryDate.isSameOrAfter(now, 'day')) {
      if (showExtendedHours) {
        to = endQueryDate.tz(ET).add(1, 'day').startOf('day');
      } else {
        to = endQueryDate.tz(ET).hour(16).minute(0).second(0).millisecond(0);
      }
    }

    if (!startQueryDate.isSameOrBefore(now, 'day')) {
      return;
    }
    let from;
    if (showExtendedHours) {
      from = startQueryDate.tz(ET).startOf('day');
    } else {
      from = startQueryDate.tz(ET).hour(9).minute(30).second(0).millisecond(0);
    }
    datafeed.current.onVisibleRangeChanged({
      from,
      to,
    });
    resetZoomRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startQueryDate, endQueryDate]);

  const currentGlobalStocksState = useRecoilValue(
    stocksState_SUBSCRIBE_TO_POLLING_RENDER,
  );
  const setCurrentSymHiroOverview = useSetRecoilState(
    currentSymHiroOverviewState,
  );
  const setHiroAlerts = useSetRecoilState(hiroAlertsState);

  const { fetchAPIWithLog } = useLog('HiroChart');

  // eslint-disable-next-line prefer-const
  let [rerenderCnt, setRerenderCnt] = useState(0);

  useEffect(() => {
    const currentSession = showExtendedHours ? 'extended' : 'regular';
    if (
      datafeed.current != null &&
      datafeed.current.currentSession != currentSession
    ) {
      datafeed.current.currentSession = currentSession;
      datafeed.current.resetData();
      let from = datafeed.current.beginVisibleRange;
      if (currentSession == 'extended') {
        from = from.tz(ET).startOf('day');
      } else {
        from = from.tz(ET).hour(9).minute(30).second(0).millisecond(0);
      }
      let to = datafeed.current.endVisibleRange;
      if (from > dayjs()) {
        from = from.subtract(1, 'day');
        to = from.hour(4).minute(0).second(0).millisecond(0);
      }
      datafeed.current.onVisibleRangeChanged({
        from,
        to,
      });
      resetZoomRef.current?.();
      setTimeout(() => resetZoomRef.current?.(), 5_000);
    }
  }, [showExtendedHours]);

  if (datafeed.current == null) {
    datafeed.current = new HiroChartDatafeed(
      hiroRollingWindowSeconds / 60,
      setHiroAlerts,
      () => setRerenderCnt(++rerenderCnt),
      showExtendedHours ? 'extended' : 'regular',
      resetZoomRef,
    );

    datafeed.current.fetchAPIWithLog = fetchAPIWithLog;
    // throttle broadcasting too much as dependent code launches fetches immediately
    datafeed.current.broadCastTimeRangeUpdate = debounce(setHiroTimeRange, 200);
  }
  useEffect(
    () =>
      datafeed.current.setRollingWindowMinutes(hiroRollingWindowSeconds / 60),
    [hiroRollingWindowSeconds],
  );

  const { sendStreamControlMsg, reconnectSocket } = useStreamingSocketCore({
    enabled: true,
    socket: useRef<WebSocket | null>(null),
    socketRetries: useRef(0),
    onOpen: () => datafeed.current.reRegisterCandleSubscriptions(),
    onReconnect: () => datafeed.current.reRegisterCandleSubscriptions(),
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    updateCandleCallback: (...args) =>
      datafeed.current.listenCandlesStream(...args),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reconnectSocket, []);

  const currentDatafeed = datafeed.current;

  datafeed.current.sendStreamControlMsg = sendStreamControlMsg;

  datafeed.current.currentGlobalStocksState = currentGlobalStocksState;
  datafeed.current.setCurrentSymHiroOverview = setCurrentSymHiroOverview;

  useEffect(() => {
    if (currentDatafeed != null && webWorker != null) {
      currentDatafeed.setWebWorker(webWorker);
    }
  }, [webWorker, currentDatafeed]);

  useEffect(() => {
    datafeed.current.resetData();
    for (const { symbolLineParams } of Object.values(
      datafeed.current.signalSeries,
    )) {
      currentDatafeed.disableSignalSeries(symbolLineParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sym]);

  useEffect(() => {
    currentDatafeed.enableSignalSeries({
      kind: 'price',
      symbol: sym,
    });

    const deltaSymbolParams: Omit<SymbolParams, deltaLineParams> = {
      kind: 'hiro',
      symbol: sym,
    };

    (allTradesEnabled && totalOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Total',
        optionOrigin: 'All',
        isFiltered: true,
      },
    });

    (allTradesEnabled && putOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Puts',
        optionOrigin: 'All',
        isFiltered: true,
      },
    });

    (allTradesEnabled && callOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Calls',
        optionOrigin: 'All',
        isFiltered: true,
      },
    });

    (nextExpiryEnabled && totalOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Total',
        optionOrigin: 'NextExpiry',
        isFiltered: true,
      },
    });

    (nextExpiryEnabled && putOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Puts',
        optionOrigin: 'NextExpiry',
        isFiltered: true,
      },
    });

    (nextExpiryEnabled && callOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Calls',
        optionOrigin: 'NextExpiry',
        isFiltered: true,
      },
    });

    update({
      hiroSelectedSeriesList: Object.keys(currentDatafeed.signalSeries)
        .sort()
        .map((seriesName) => ({
          seriesName,
          symbolLineParams:
            currentDatafeed.signalSeries[seriesName].symbolLineParams,
        })),
    });

    setRerenderCnt(-rerenderCnt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allTradesEnabled,
    nextExpiryEnabled,
    totalOptionAccumulationEnabled,
    putOptionAccumulationEnabled,
    callOptionAccumulationEnabled,
    sym,
    currentDatafeed,
  ]);

  for (const series of Object.values(currentDatafeed.signalSeries)) {
    if (series.symbolLineParams.symbol != sym) {
      currentDatafeed.boundDisableSignalSeries(series.symbolLineParams);
    }
  }

  const beginVisibleRange = datafeed.current.beginVisibleRange.valueOf();
  const endVisibleRange = datafeed.current.endVisibleRange?.valueOf();

  const validHiroIndicators = hiroIndicators
    .filter(
      (hiroIndicator) =>
        Object.values(datafeed.current.signalSeries).some((series) =>
          genericSymbolineParamsEqual(
            hiroIndicator.symbolLineParams,
            series.symbolLineParams,
          ),
        ) && hiroIndicatorValid(hiroIndicator),
    )
    .map((hiroIndicator) => {
      const indicatorMeta = indicatorFromName.get(hiroIndicator.indicatorName)!;
      return [
        hiroIndicator,
        indicatorMeta,
        range(
          Math.max(
            hiroIndicator.constructorInputs?.length ?? 0,
            indicatorMeta?.constructorInputs.length ?? 0,
          ),
        ).map((i) => {
          const userInput = hiroIndicator.constructorInputs?.[i];
          const paramMeta = indicatorMeta.constructorInputs[i];

          let constructorVal;

          if (userInput != null && userInput.length > 0) {
            constructorVal = parseFloat(userInput);
            if (isNaN(constructorVal)) {
              constructorVal = paramMeta?.defaultValue;
            } else {
              constructorVal =
                paramMeta.inputTransform?.(constructorVal) ?? constructorVal;
            }
          } else {
            constructorVal = paramMeta?.defaultValue;
          }
          return {
            userInput,
            paramMeta,
            constructorVal,
          };
        }),
      ];
    });

  const maxIndicatorsInterval = Math.max(
    0,
    ...validHiroIndicators.flatMap(
      ([_hiroIndicator, _indicatorMeta, constructorParams]) =>
        constructorParams
          .filter(
            ({ paramMeta, constructorVal }): boolean =>
              ((paramMeta?.paramName as string) ?? '').includes('Interval') &&
              constructorVal != null &&
              !isNaN(constructorVal),
          )
          .map(({ constructorVal }) => constructorVal),
    ),
  );

  const indicatorContextBegin =
    beginVisibleRange - maxIndicatorsInterval * candleDurationMs;

  const deltaSeriesList = Object.keys(datafeed.current.signalSeries)
    .sort()
    .filter(
      (name) =>
        datafeed.current.signalSeries[name].symbolLineParams.kind === 'hiro',
    )
    .map((name) => {
      const series = datafeed.current.signalSeries[name];
      const { optionAccumulationType, optionOrigin } =
        series.symbolLineParams.deltaLineParams;
      const color = theme.palette.hiro.lenses[
        OPTION_ORIGIN_TO_PALETTE.get(optionOrigin)!
      ][OPTION_ACCUM_TO_PALETTE.get(optionAccumulationType)!] as string;
      return new DeltaChartSeries(
        name,
        series.symbolLineParams,
        series.mutatedCandles?.filter(
          (c) =>
            c.time >= indicatorContextBegin &&
            (endVisibleRange == null || c.time <= endVisibleRange) &&
            (showExtendedHours || skipNonMarketHours.dateInMarketHours(c.date)),
        ) ?? [],
        [color],
      );
    });

  const priceSeriesList = Object.keys(datafeed.current.signalSeries)
    .sort()
    .filter(
      (name) =>
        datafeed.current.signalSeries[name].symbolLineParams.kind === 'price',
    )
    .map((name) => {
      const series = datafeed.current.signalSeries[name];

      return new PriceChartSeries(
        name,
        series.symbolLineParams,
        series.mutatedCandles?.filter(
          (c) =>
            c.time >= indicatorContextBegin &&
            (endVisibleRange == null || c.time <= endVisibleRange) &&
            (showExtendedHours || skipNonMarketHours.dateInMarketHours(c.date)),
        ) ?? [],
        [theme.colorMode === ColorMode.LIGHT ? 'black' : 'white'],
        [theme.colorMode === ColorMode.LIGHT ? 'white' : 'black'],
        priceLineType,
      );
    });

  const indicatorList = validHiroIndicators
    .map(([hiroIndicator, indicatorMeta, constructorParams]) => {
      if (indicatorMeta == null) {
        return null;
      }
      const series = Object.values(datafeed.current.signalSeries).find(
        (series) =>
          genericSymbolineParamsEqual(
            hiroIndicator.symbolLineParams,
            series.symbolLineParams,
          ),
      );

      if (series == null) {
        return;
      }
      const yScale =
        indicatorMeta.panePlacement != 'Main'
          ? 'own'
          : series.symbolLineParams.kind === 'price'
            ? 'price'
            : 'delta';

      const inputChartSeries = (
        yScale === 'own'
          ? [...priceSeriesList, ...deltaSeriesList]
          : yScale === 'price'
            ? priceSeriesList
            : deltaSeriesList
      ).find((series) =>
        genericSymbolineParamsEqual(
          series.symbolLineParams,
          hiroIndicator.symbolLineParams,
        ),
      );

      if (inputChartSeries == null) {
        return null;
      }

      let indicatorCls = indicatorMeta.cls;
      const isWrapperedBollingBands =
        hiroIndicator.indicatorName == 'Bollinger Bands Width (BBW)';
      if (isWrapperedBollingBands) {
        indicatorCls = trading_signals.BollingerBands;
      }

      let indicatorKernel = new indicatorCls(
        ...constructorParams.map(({ constructorVal }) => constructorVal),
      );
      if (isWrapperedBollingBands) {
        indicatorKernel = new indicatorMeta.cls(indicatorKernel);
      }

      let exceptionSeen = false;
      const data = inputChartSeries.candles
        .map((candle) => {
          if (exceptionSeen) {
            return;
          }
          let value;
          try {
            switch (indicatorMeta.inputType) {
              case 'number':
                value = indicatorKernel.add(candle.close);
                break;
              case 'HighLowClose':
              case 'HighLow':
              case 'OpenHighLowCloseVolume':
              case 'HighLowCloseVolume':
                value = indicatorKernel.add(candle);
                break;
              default:
                throw Exception('Not implemented');
            }
          } catch (_e: Exception) {
            exceptionSeen = true;
          }

          if (value == null || (typeof value == 'number' && isNaN(value))) {
            return null;
          } else {
            if (indicatorMeta.resultType == 'number') {
              return {
                date: candle.date,
                time: candle.time,
                value,
              };
            } else {
              return {
                date: candle.date,
                time: candle.time,
                ...value,
              };
            }
          }
        })
        .filter(Boolean);

      let chartSeriesClass;
      switch (indicatorMeta.resultType) {
        case 'number':
          chartSeriesClass = IndicatorSingleLineChartSeries;
          break;
        case 'BandsResult':
          chartSeriesClass = BollingerBandsChartSeries;
          break;

        case 'MACDResult':
          chartSeriesClass = MACDChartSeries;
          break;
        default:
          throw Error(
            `Unsupported chart series type ${indicatorMeta.indicatorName}`,
          );
      }

      return new chartSeriesClass(
        `${hiroIndicator.seriesName} - ${hiroIndicator.indicatorName}`,
        yScale,
        indicatorMeta,
        data,
        hiroIndicator.colors,
      );
    })
    .filter(Boolean);

  if (maxIndicatorsInterval > 0) {
    for (const series of [...deltaSeriesList, ...priceSeriesList]) {
      series.candles = series.candles.filter(
        (c) => c.time >= beginVisibleRange,
      );
    }
  }

  const beginVisibleRangeBegDay = datafeed.current.beginVisibleRange
    .tz(ET)
    .startOf('day');
  const endVisibleRangeEndDay = (datafeed.current.endVisibleRange ?? dayjs())
    .add(1, 'day')
    .tz(ET)
    .startOf('day');

  const { data: hiroKeyLevelList } = useHiroKeyLevels(
    {
      sym,
      beginDate: beginVisibleRangeBegDay,
      endDate: endVisibleRangeEndDay,
    },
    {
      enabled: showKeyLevels,
    },
  );
  const hiroKeyLevels = new KeyLevelChartSeries(
    'hiroKeyLevels',
    showKeyLevels ? (hiroKeyLevelList ?? []) : [],
  );

  const { data: alerts } = useQuery({
    placeholderData: keepPreviousData,
    enabled: showAlerts,
    queryKey: [
      'HiroChartV3Alerts',
      {
        sym,
        beginDate: beginVisibleRangeBegDay.valueOf(),
        endDate: endVisibleRangeEndDay.valueOf(),
      },
    ],
    queryFn: async (): Promise<AlertData[]> =>
      fetchAPIWithLog<AlertData[]>(
        `v1/alerts?syms=${encodeURIComponent(sym)}&start=${beginVisibleRangeBegDay.format('YYYY-MM-DD')}&end=${endVisibleRangeEndDay.format('YYYY-MM-DD')}`,
      )
        .then(filterHiroAlerts)
        .then((alerts: AlertData[]) =>
          alerts.map((alert) => ({
            alert_time: new Date(alert.alert_time),
            category: HiroAlertCategoryFromAlertCategory[
              alert.category
            ] as string,
          })),
        ),
  });

  const alertSeries = new AlertsChartSeries(showAlerts ? (alerts ?? []) : []);

  useEffect(
    () => {
      if (webWorker == null) {
        return;
      }
      return unsupressedSetInterval(
        webWorker,
        datafeed.current.debouncedRerenderChart,
        5_000,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [webWorker, datafeed.current],
  );

  // clean up polling artifacts on unmount of the component
  useEffect(
    () => () => {
      if (datafeed.current == null) {
        return;
      }
      for (const series of Object.values(datafeed.current.signalSeries)) {
        datafeed.current.boundDisableSignalSeries(series.symbolLineParams);
      }
    },
    [],
  );

  return (
    <HiroD3Chart
      chartId="hiro-v3-chart"
      sym={sym}
      deltaSeriesList={deltaSeriesList}
      priceSeriesList={priceSeriesList}
      mainPaneIndicatorList={indicatorList.filter(
        (i) => i.indicatorMeta.panePlacement == 'Main',
      )}
      bottomIndicatorList={indicatorList.filter(
        (i) => i.indicatorMeta.panePlacement == 'Bottom',
      )}
      hiroKeyLevels={hiroKeyLevels}
      alertSeries={alertSeries}
      rerenderCnt={rerenderCnt}
      datafeedRef={datafeed}
      hiroLineType={hiroLineType}
      resetZoomRef={resetZoomRef}
    />
  );
};

const drawYAxisClosingVals = (
  yAxisSvg,
  chartSeriesList: ChartSeries[],
  scale,
  formatter: (n: number) => string,
) => {
  const labels = yAxisSvg
    .selectAll('g.hiro-closing-y-axis-label')
    .data(chartSeriesList);

  labels.exit().remove();

  const gEnter = labels
    .enter()
    .append('g')
    .attr('class', 'hiro-closing-y-axis-label')
    .attr('z-index', 100);
  gEnter
    .append('rect')
    .attr('width', '100%')
    .attr('height', '13')
    .attr('transform', 'translate(0, -4.5)');
  gEnter
    .append('text')
    .attr('dy', '0.55em')
    .attr('transform', 'translate(0,0)')
    .attr('font-size', '11');

  labels
    .merge(gEnter)
    .attr('transform', (d) => {
      const closingValue = d.candles[d.candles.length - 1]?.close ?? 0;
      const yTranslate = scale(closingValue);
      if (isNaN(yTranslate)) {
        return undefined;
      } else {
        return `translate(0, ${yTranslate})`;
      }
    })
    .attr('fill', (d) => d.colors[0])
    .style('opacity', (d) =>
      typeof d.candles[d.candles.length - 1]?.close == 'undefined' ? '0' : '1',
    )
    .select('text')
    .attr('fill', (series) => series.latestValueLabelColor ?? 'white')
    .text((d) =>
      formatter(d.candles[d.candles.length - 1]?.close ?? 0, {
        maximumSignificantDigits: 3,
      }),
    );
};

const currencyLabel = (n) =>
  Intl.NumberFormat('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const deltaLabel = (n) =>
  formatAsCompactNumber(n, { maximumSignificantDigits: 3 });

const formatMillisecond = '.%L';
const formatSecond = ':%S';
const formatMinute = '%I:%M';
const formatHour = '%_I %p';
const formatDay = '%x';
const formatWeek = '%x';
const formatMonth = '%x';
const formatYear = '%x';

function getTimeFormatter(date, beginningOfDay) {
  let subDayFmt;
  if (timeSecond(date) < date) {
    subDayFmt = formatMillisecond;
  } else if (timeMinute(date) < date) {
    subDayFmt = formatSecond;
  } else if (timeHour(date) < date) {
    subDayFmt = formatMinute;
  } else if (timeDay(date) < date) {
    subDayFmt = formatHour;
  } else if (timeMonth(date) < date) {
    return timeWeek(date) < date ? formatDay : formatWeek;
  } else if (timeYear(date) < date) {
    return formatMonth;
  } else {
    return formatYear;
  }

  if (date < beginningOfDay) {
    return `%a ${subDayFmt}`;
  } else {
    return subDayFmt;
  }
}

function timeTickFormat(date, beginningOfDay) {
  return timeFormat(getTimeFormatter(date, beginningOfDay))(date).toLowerCase();
}

const genTimeTickFormat = (timeZone) => {
  const beginningOfDay = new Date(
    dayjs().tz(timeZone).startOf('day').toDate().toLocaleString('en-US', {
      timeZone,
    }),
  );

  return (date) =>
    timeTickFormat(
      new Date(
        date.toLocaleString('en-US', {
          timeZone,
        }),
      ),
      beginningOfDay,
    );
};

// a formatting of the indicator output for display
// multiple results means multiple lins
const indicatorDisplayStrings = (
  resultType: IndicatorResultType,
  yScale: 'price' | 'delta' | 'own',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datum: any,
): string[] => {
  const fmt = yScale == 'hiro' ? deltaLabel : currencyLabel;
  switch (resultType) {
    case 'number':
      return [fmt(datum.value)];
    case 'BandsResult':
      return [
        `Lower: ${fmt(datum.lower)}`,
        `Middle: ${fmt(datum.middle)}`,
        `Upper: ${fmt(datum.upper)}`,
      ];
    case 'DMAResult':
      return [`Long: ${fmt(datum.long)}`, `Short: ${fmt(datum.short)}`];
    case 'MACDResult':
      return [
        `Histogram: ${fmt(datum.histogram)}`,
        `MACD: ${fmt(datum.macd)}`,
        `Signal: ${fmt(datum.signal)}`,
      ];
    case 'LinearRegressionResult':
      return [
        `Prediction: ${fmt(datum.prediction)}`,
        `Slope: ${fmt(datum.slope)}`,
        `Intercept: ${fmt(datum.intercept)}`,
      ];
    case 'StochasticResult':
      return [`D: ${fmt(datum.stochD)}`, `K: ${fmt(datum.stochK)}`];
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw Exception(`Unsupported resultType=${resultType}`);
  }
};
